import { api, BASE_URL, getToken } from './client';
import type { AccountBalanceDto } from '../types/report';

export interface VatSummaryDto {
  periodFrom: string;
  periodTo: string;
  totalSales: number;
  salesVat: number;
  totalPurchases: number;
  purchaseVat: number;
  netVatPayable: number;
}

export interface VatDeclarationDto {
  periodFrom: string;
  periodTo: string;
  totalSalesExcludingVat: number;
  salesVat21: number;
  salesVat9: number;
  salesVat5: number;
  salesVat0: number;
  totalSalesVat: number;
  totalPurchasesExcludingVat: number;
  purchaseVat21: number;
  purchaseVat9: number;
  purchaseVat5: number;
  totalPurchaseVat: number;
  netVatPayable: number;
}

export interface VatDeclarationLineDto {
  vatRate: number;
  taxableAmount: number;
  vatAmount: number;
}

export interface DebtAgingBucketDto {
  bucketName: string;
  daysFrom: number;
  daysTo: number;
  amount: number;
  personCount: number;
}

export interface DebtAgingPersonDto {
  person: {
    id: number;
    name: string;
    code: string;
  };
  totalOutstanding: number;
  currentAmount: number;
  bucket1to30: number;
  bucket31to60: number;
  bucket61to90: number;
  bucketOver90: number;
}

export interface DebtAgingDto {
  type: string;
  asOfDate: string;
  totalOutstanding: number;
  buckets: DebtAgingBucketDto[];
  personDetails: DebtAgingPersonDto[];
}

export interface IncomeStatementLineDto {
  accountNumber: number;
  accountName: string;
  amount: number;
}

export interface BalanceSheetLineDto {
  accountNumber: number;
  accountName: string;
  debitBalance: number;
  creditBalance: number;
  netBalance: number;
}

export interface BalanceSheetSectionDto {
  sectionName: string;
  classCode: string;
  total: number;
  lines: BalanceSheetLineDto[];
}

export interface FinancialStatementsDto {
  incomeStatement: {
    periodFrom: string;
    periodTo: string;
    totalIncome: number;
    totalExpenses: number;
    netProfit: number;
    incomeLines: IncomeStatementLineDto[];
    expenseLines: IncomeStatementLineDto[];
  };
  balanceSheet: {
    asOfDate: string;
    totalAssets: number;
    totalEquityAndLiabilities: number;
    sections: BalanceSheetSectionDto[];
  };
}

// ── Reports package 2026-09-13: kasos knyga, sąskaitų išrašas, suderinimo aktas.

export interface CashBookRowDto {
  date: string;
  documentNumber: string;
  content: string;
  personName: string | null;
  income: number | null;
  expense: number | null;
}

export interface CashBookDto {
  account: number;
  from: string;
  to: string;
  openingBalance: number;
  rows: CashBookRowDto[];
  totalIncome: number;
  totalExpense: number;
  closingBalance: number;
}

export interface AccountStatementRowDto {
  date: string;
  documentNumber: string;
  content: string;
  debit: number;
  credit: number;
  personName: string | null;
  correspondence: string;
}

export interface AccountStatementDto {
  account: number;
  from: string;
  to: string;
  personId: number | null;
  includeSubAccounts: boolean;
  debitBalanceStart: number;
  creditBalanceStart: number;
  debitTurnover: number;
  creditTurnover: number;
  debitBalanceEnd: number;
  creditBalanceEnd: number;
  rows: AccountStatementRowDto[];
}

export interface DebtStatementPersonSummaryDto {
  personId: number;
  personName: string;
  personCode: string | null;
  personAddress: string | null;
  balanceEnd: number;
}

export const DEBT_STATEMENT_ITEM_TYPE = {
  BalanceStart: 0,
  Transaction: 1,
  Turnover: 2,
  BalanceEnd: 3,
} as const;

export interface DebtStatementRowDto {
  itemType: number;
  date: string | null;
  documentNumber: string | null;
  content: string;
  debit: number;
  credit: number;
}

export interface DebtStatementDto {
  account: number;
  from: string;
  to: string;
  personId: number;
  personName: string;
  personCode: string | null;
  personAddress: string | null;
  statementNumber: string;
  rows: DebtStatementRowDto[];
}

/** Authenticated CSV download (blob, UTF-8 BOM) — window.open carries no token. */
async function downloadCsv(path: string, fileName: string): Promise<void> {
  const headers: Record<string, string> = { Accept: 'text/csv' };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(`${BASE_URL}${path}`, {
    headers,
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`Eksportas nepavyko (${response.status})`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export const reportsApi = {
  trialBalance: (params: { from: string; to: string; account?: number; nonZeroOnly?: boolean }) =>
    api.get<AccountBalanceDto[]>('/reports/trial-balance', {
      from: params.from,
      to: params.to,
      account: params.account,
      nonZeroOnly: params.nonZeroOnly ?? true,
    }),

  vatSummary: (params: { from: string; to: string }) =>
    api.get<VatSummaryDto>('/reports/vat-summary', {
      from: params.from,
      to: params.to,
    }),

  vatDeclaration: (params: { from: string; to: string }) =>
    api.get<VatDeclarationDto>('/reports/vat-declaration', {
      from: params.from,
      to: params.to,
    }),

  debtAging: (params: { asOf: string; type?: string }) =>
    api.get<DebtAgingDto>('/reports/debt-aging', {
      asOf: params.asOf,
      type: params.type ?? 'receivable',
    }),

  financialStatements: (params: { from: string; to: string }) =>
    api.get<FinancialStatementsDto>('/reports/financial-statements', {
      from: params.from,
      to: params.to,
    }),

  exportSaft: (params: { format?: string; from?: string; to?: string }) => {
    const searchParams = new URLSearchParams();
    searchParams.set('format', params.format ?? 'SAFT2.0');
    if (params.from) searchParams.set('from', params.from);
    if (params.to) searchParams.set('to', params.to);
    window.open(`${BASE_URL}/reports/export?${searchParams.toString()}`, '_blank');
  },

  cashBook: (params: { from: string; to: string; account: number }) =>
    api.get<CashBookDto>('/reports/cash-book', {
      from: params.from,
      to: params.to,
      account: params.account,
    }),

  cashBookCsv: (params: { from: string; to: string; account: number }) =>
    downloadCsv(
      `/reports/cash-book?from=${params.from}&to=${params.to}&account=${params.account}&format=csv`,
      `kasos-knyga-${params.account}-${params.from}.csv`,
    ),

  accountStatement: (params: { from: string; to: string; account: number; personId?: number }) =>
    api.get<AccountStatementDto>('/reports/account-statement', {
      from: params.from,
      to: params.to,
      account: params.account,
      personId: params.personId,
    }),

  accountStatementCsv: (params: { from: string; to: string; account: number; personId?: number }) =>
    downloadCsv(
      `/reports/account-statement?from=${params.from}&to=${params.to}&account=${params.account}` +
        (params.personId ? `&personId=${params.personId}` : '') + '&format=csv',
      `saskaitos-irasas-${params.account}-${params.from}.csv`,
    ),

  debtStatementPersons: (params: { from: string; to: string; account: number }) =>
    api.get<DebtStatementPersonSummaryDto[]>('/reports/debt-statement/persons', {
      from: params.from,
      to: params.to,
      account: params.account,
    }),

  debtStatement: (params: { from: string; to: string; account: number; personId: number }) =>
    api.get<DebtStatementDto>('/reports/debt-statement', {
      from: params.from,
      to: params.to,
      account: params.account,
      personId: params.personId,
    }),

  debtStatementCsv: (params: { from: string; to: string; account: number; personId: number }) =>
    downloadCsv(
      `/reports/debt-statement?from=${params.from}&to=${params.to}&account=${params.account}&personId=${params.personId}&format=csv`,
      `suderinimo-aktas-${params.to.replaceAll('-', '')}-${params.personId}.csv`,
    ),
};
